import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock3, UserRound } from "lucide-react";
import { UserContext } from "contexts/UserContext";

interface LaunchableAboutProps {
  authorUserId?: string;
  updatedAt?: string;
}

const formatUpdatedAt = (value?: string) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const label = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return { label, title: label };
};

export const LaunchableAbout: React.FC<LaunchableAboutProps> = ({
  authorUserId,
  updatedAt,
}) => {
  const { getUser } = useContext(UserContext);
  const { data: author } = useQuery({
    queryKey: ["launchable-author", authorUserId],
    queryFn: () => getUser(authorUserId!),
    enabled: Boolean(authorUserId),
    staleTime: 5 * 60 * 1000,
  });
  const authorUsername = author?.username?.trim();
  const formattedUpdatedAt = formatUpdatedAt(updatedAt);

  return (
    <section aria-labelledby="launchable-about-heading">
      <h2
        id="launchable-about-heading"
        className="text-sm font-semibold text-primary"
      >
        About
      </h2>
      <div className="mt-3 space-y-3 text-sm leading-5 text-secondary">
        {authorUsername && (
          <div className="flex items-start gap-2">
            <UserRound
              className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500"
              aria-hidden="true"
            />
            <p className="min-w-0">
              Created by{" "}
              <span className="font-medium text-primary">{authorUsername}</span>
            </p>
          </div>
        )}
        {formattedUpdatedAt && (
          <div className="flex items-start gap-2">
            <Clock3
              className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500"
              aria-hidden="true"
            />
            <p>
              Last updated{" "}
              <time dateTime={updatedAt} title={formattedUpdatedAt.title}>
                {formattedUpdatedAt.label}
              </time>
            </p>
          </div>
        )}
      </div>
    </section>
  );
};
